import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardProps,
  Center,
  Flex,
  Heading,
  IconButton,
  Spacer,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { AfeTransactionRoutingSetting } from 'api/bindings/AfeTransactionRoutingSetting';
import { InvoiceTransactionRoutingSetting } from 'api/bindings/InvoiceTransactionRoutingSetting';
import { PurchaseOrderTransactionRoutingSetting } from 'api/bindings/PurchaseOrderTransactionRoutingSetting';
import { Role } from 'api/bindings/Role';
import { RoleResponse } from 'api/bindings/RoleResponse';
import { TransactionRoutingSettingsResponse } from 'api/bindings/TransactionRoutingSettingsResponse';
import { User } from 'api/bindings/User';
import { UserResponse } from 'api/bindings/UserResponse';
import { WorkOrderTransactionRoutingSetting } from 'api/bindings/WorkOrderTransactionRoutingSetting';
import { useMemo } from 'react';
import { FiEdit, FiTrash } from 'react-icons/fi';
import { getDisplayName } from 'types';

interface RowDisplayProps {
  user: User | UserResponse | undefined | null;
  role: Role | RoleResponse | undefined | null;
}
const RowDisplay = (props: RowDisplayProps) => {
  const { user, role } = props;

  if (role && user) {
    return (
      <Text>
        {role.name}: {getDisplayName(user)}
      </Text>
    );
  } else if (user) {
    return <Text>{getDisplayName(user)}</Text>;
  } else {
    return <Text>N/A</Text>;
  }
};

export interface ClientApproversSectionProps<
  // T extends CoreTransactionRoutingSettings
  T extends
    | AfeTransactionRoutingSetting
    | PurchaseOrderTransactionRoutingSetting
    | WorkOrderTransactionRoutingSetting
    | InvoiceTransactionRoutingSetting
> extends CardProps {
  clientId: string | undefined;
  heading: string;
  settings: TransactionRoutingSettingsResponse<T> | undefined;
  assigneeApprovalOptional: boolean | undefined;
  onEdit: () => void;
  onDelete: () => void;
}
export const ClientApproversSection = <
  // T extends CoreTransactionRoutingSettings
  T extends
    | AfeTransactionRoutingSetting
    | PurchaseOrderTransactionRoutingSetting
    | WorkOrderTransactionRoutingSetting
    | InvoiceTransactionRoutingSetting
>(
  props: ClientApproversSectionProps<T>
) => {
  const {
    clientId,
    settings,
    heading,
    assigneeApprovalOptional = false,
    onEdit,
    onDelete,
    ...rest
  } = props;

  const isConfigured = useMemo(() => {
    const { stepSetId, assigneeApprovalRequired } = settings || {};
    return stepSetId || (assigneeApprovalOptional && assigneeApprovalRequired);
  }, [assigneeApprovalOptional, settings]);

  return (
    <Card
      as="section"
      bg="bg-surface"
      borderRadius={'lg'}
      boxShadow={'2xl'}
      {...rest}
    >
      <CardHeader>
        <Flex gap={4}>
          <Heading noOfLines={1} size="sm">
            {heading}
          </Heading>
          {(isConfigured && (
            <Badge colorScheme="green" alignSelf={'center'}>
              Configured
            </Badge>
          )) || (
            <Badge colorScheme="red" alignSelf={'center'}>
              Not Configured
            </Badge>
          )}
          <Spacer />
          <IconButton
            colorScheme="blue"
            variant={'solid'}
            icon={
              // @ts-ignore
              <FiEdit />
            }
            aria-label={'Edit Settings'}
            onClick={onEdit}
          />
          <IconButton
            colorScheme="red"
            icon={
              // @ts-ignore
              <FiTrash />
            }
            aria-label="Delete Settings"
            onClick={onDelete}
            isDisabled={!settings?.stepSetId}
          />
        </Flex>
      </CardHeader>
      <CardBody>
        <Stack gap={[2, 4]}>
          <Flex gap={[2, 4]}>
            {assigneeApprovalOptional && (
              <>
                <Text as="b">Asignee Approval Required:</Text>
                <Text>{settings?.assigneeApprovalRequired ? 'Yes' : 'No'}</Text>
              </>
            )}
          </Flex>
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th w={8}>No.</Th>
                  <Th>Approver</Th>
                </Tr>
              </Thead>
              <Tbody>
                {!settings?.stepSetId && (
                  <Tr>
                    <Td colSpan={2}>
                      <Center>No Additional Approvers Configured</Center>
                    </Td>
                  </Tr>
                )}
                {settings?.stepSet?.steps.map((step, i) => (
                  <Tr key={i}>
                    <Td>{i + 1}</Td>
                    <Td>
                      <RowDisplay role={step.role} user={step.user} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Stack>
      </CardBody>
    </Card>
  );
};
